/**
 * FLK Theme Manager
 * Centralized theme management for TFN, SUPER, and DYNAMIC FLK forms.
 */

import { applyBodyClass, removeBodyClass } from "./themeUtils";

export type FlkFormType = "TFN" | "SUPER" | "DYNAMIC" | null;

const FLK_THEME_CLASSES = {
    TFN: "tfnForm",
    SUPER: "theme-form-id1",
    DYNAMIC: null, // Dynamic forms use custom class names from flk_config
} as const;

const ALL_FLK_THEME_CLASSES = [
    FLK_THEME_CLASSES.TFN,
    FLK_THEME_CLASSES.SUPER,
] as const;

/**
 * Get the theme class name for a given FLK form type
 * 
 * @param formType - The FLK form type
 * @returns The theme class name, or null for DYNAMIC forms (which use custom classes)
 */
export const getThemeClassForFormType = (formType: FlkFormType): string | null => {
    if (!formType) {return null;}
    return FLK_THEME_CLASSES[formType];
};

/**
 * Remove all FLK-related theme classes from the body
 * 
 * This ensures a clean slate before applying a new theme.
 */
export const removeAllFlkThemes = (): void => {
    ALL_FLK_THEME_CLASSES.forEach((className) => {
        removeBodyClass(className);
    });
};

/**
 * Apply the appropriate theme for a given FLK form type
 * 
 * This function:
 * 1. Removes all existing FLK theme classes
 * 2. Applies the correct theme class for the form type
 * 3. For DYNAMIC forms, applies the custom class name if provided
 * 
 * @param formType - The FLK form type (TFN, SUPER, DYNAMIC, or null)
 * @param customClassName - For DYNAMIC forms, the custom class name from flk_config.className
 * 
 * @example
 * // Apply TFN theme
 * applyFlkTheme("TFN");
 * 
 * @example
 * // Apply SUPER theme
 * applyFlkTheme("SUPER");
 * 
 * @example
 * // Apply DYNAMIC theme with custom class
 * applyFlkTheme("DYNAMIC", "custom-dynamic-theme");
 * 
 * @example
 * // Remove all FLK themes
 * applyFlkTheme(null);
 */
export const applyFlkTheme = (
    formType: FlkFormType,
    customClassName?: string | null
): void => {
    // Always remove all existing FLK themes first
    removeAllFlkThemes();

    // Apply the appropriate theme based on form type
    if (!formType) {
        return; // No theme to apply
    }

    if (formType === "TFN") {
        applyBodyClass(FLK_THEME_CLASSES.TFN);
    } else if (formType === "SUPER") {
        applyBodyClass(FLK_THEME_CLASSES.SUPER);
    } else if (formType === "DYNAMIC" && customClassName) {
        applyBodyClass(customClassName);
    }
};

/**
 * Apply theme based on form data object
 * 
 * This is a convenience function that detects the form type from a form data object
 * and applies the appropriate theme.
 * 
 * @param formData - The form data object with isTFNForm, isSuperForm, or flk_config properties
 * 
 * @example
 * // Apply theme based on form data
 * const formData = { isTFNForm: 1, ... };
 * applyThemeFromFormData(formData);
 */
export const applyThemeFromFormData = (formData: any): void => {
    if (!formData) {
        removeAllFlkThemes();
        return;
    }

    // Detect form type
    let formType: FlkFormType = null;
    let customClassName: string | null = null;

    if (formData.isTFNForm === 1) {
        formType = "TFN";
    } else if (formData.isSuperForm === 1) {
        formType = "SUPER";
    } else if (formData.flk_config?.isDynamicFlkForm) {
        formType = "DYNAMIC";
        customClassName = formData.flk_config?.className || null;
    }

    // Apply the theme
    applyFlkTheme(formType, customClassName);
};
